/**
 * Display helpers for the Tasks dashboard cards.
 *
 * Titles wrap the layout when they are very long, so the dashboard shows
 * a 50-character name and keeps the full title on the native hover
 * tooltip. Each card shows either the completion date or, when that is
 * missing, the creation date — labelled so the two cannot be confused.
 */

export const TASK_TITLE_MAX_CHARS = 50;

const COMPLETE_DATE_OPTIONS = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

/**
 * Parse an API ``YYYY-MM-DD`` date into a local-midnight ``Date``.
 *
 * ``new Date('2026-01-31')`` is UTC midnight by spec, so it can render
 * as the previous day west of UTC. Splitting the parts pins it to local
 * midnight, which is what a date-only field means to a user.
 */
export function parseApiDate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** ``19 September 2026`` — always includes the year, never relative wording. */
export function formatCompleteDate(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  if (!raw) return '';

  let date = null;
  if (/T/.test(raw) || /Z|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  }
  if (!date) date = parseApiDate(raw);
  if (!date) return '';

  return date.toLocaleDateString('en-GB', COMPLETE_DATE_OPTIONS);
}

/**
 * Date shown on a dashboard task card, with a label for what it means.
 *
 * Prefer the task's end / completion date. If that is missing, fall
 * back to the creation timestamp so the card still has a date.
 */
export function taskCardDate(task) {
  if (!task || typeof task !== 'object') return null;

  const completion = formatCompleteDate(task.end_date);
  if (completion) {
    return { kind: 'Completion date', date: completion };
  }

  const creation = formatCompleteDate(task.created_on);
  if (creation) {
    return { kind: 'Creation date', date: creation };
  }

  return null;
}

export function truncateTaskTitle(title, max = TASK_TITLE_MAX_CHARS) {
  const limit = Number.isFinite(max) && max > 0 ? max : TASK_TITLE_MAX_CHARS;
  const s = String(title || '').trim();
  if (s.length <= limit) return s;
  return `${s.slice(0, limit).trimEnd()}…`;
}
